import React, {FC, memo, MutableRefObject, useCallback, useMemo, useRef, useState} from "react"
import {I18nIndividual as GedcomIndividual, Sex} from "../../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../../model/gedcom-family"
import {FormattedMessage, useIntl} from "react-intl"
import {Button, Checkbox, Dropdown, Icon, Popup, TableCell, TableRow} from "semantic-ui-react"
import {SelectCell, sexOptions, useRelatedIndividualOptions} from "../creator-select-cell"
import {createTextCell} from "../creator-text-cell"
import {Locale} from "../../locale"
import {createDateCell} from "../creator-date-cell"
import {CreatorLocationCell} from "../creator-location-cell"
import {useLocation} from "react-router"
import {
    cascadeClearDependencies,
    removeRow,
    setParentBindings,
    shiftIdsAfter,
    shortenId
} from "../../utils/creator-utils"
import {ConfirmRemove} from "../confirm-remove"
import {EventHandlers} from "../creator"


interface IndividualRowProps {
    rowIndex: number
    selectedRowId: string | undefined
    setSelectedRowId: (id: string | undefined) => void
    row: GedcomIndividual
    lastId: MutableRefObject<number>
    data: GedcomIndividual[]
    familiesData?: GedcomFamily[]
    familiesEnabled: boolean
    childrenMap: Record<string, GedcomIndividual[]>
    spousesMap: Record<string, GedcomFamily[]>
    fullNames: Record<string, string>
    eventHandlers: EventHandlers
}

export const IndiRow: FC<IndividualRowProps> = memo(({
    rowIndex, selectedRowId, setSelectedRowId,
    row, lastId, data, familiesData, familiesEnabled,
    childrenMap, spousesMap, fullNames,
    eventHandlers
}) => {
    const location = useLocation()
    const intl = useIntl()
    const from = location.state?.from ?? null
    const rowVersion = useRef(0)
    const [rowToRemove, setRowToRemove] = useState<GedcomIndividual | undefined>()
    const [removeAnchor, setRemoveAnchor] = useState<HTMLElement | undefined>()

/* ------------------------------------------------------------------------------------------------------------------ */
/* EVENT HANDLERS                                                                                                     */
/* ------------------------------------------------------------------------------------------------------------------ */

    const handleEnabledChange = useCallback((checked: boolean) =>
        eventHandlers.onChange!(rowIndex, "enabled", checked),
    [rowIndex, eventHandlers.onChange])

    const handleSexChange = useCallback((value?: Sex) =>
        eventHandlers.onChange!(rowIndex, "sex", value),
    [rowIndex, eventHandlers.onChange])

    const handleBirthPlaceChange = useCallback((birthPlace?: string) =>
        eventHandlers.onChange!(rowIndex, "birthPlace", birthPlace),
    [rowIndex, eventHandlers.onChange])

    const handleDeceasePlaceChange = useCallback((deceasePlace?: string) =>
        eventHandlers.onChange!(rowIndex, "deceasePlace", deceasePlace),
    [rowIndex, eventHandlers.onChange])

    const onParentChange = useCallback((rowIndex: number, field: keyof GedcomIndividual | keyof GedcomFamily, value: any) => {
        eventHandlers.onChange!(rowIndex, field as keyof GedcomIndividual, value)  // super()
        setParentBindings(row, field, value, data, familiesData)
    }, [row, data, familiesData, eventHandlers])

    const handleStartFamily = useCallback(() => eventHandlers.startFamily!(row.fatherId!, row.motherId!), [row.fatherId, row.motherId, eventHandlers.startFamily])

    const handleConfirmRemove = useCallback((remove: GedcomIndividual) => {
        const rowIndex = data.findIndex(row => row.id === remove.id)
        if (rowIndex !== -1) {
            cascadeClearDependencies(remove, eventHandlers.setIndividualsData!, eventHandlers.setFamiliesData!)
            removeRow(rowIndex, lastId, eventHandlers.setIndividualsData!, GedcomIndividual)
            eventHandlers.setUnsavedChanges!(true)
            if (!from) shiftIdsAfter(GedcomIndividual, rowIndex, rowVersion, eventHandlers.setIndividualsData!)
        }
        setRowToRemove(undefined)
    }, [from, data, lastId, eventHandlers.setIndividualsData, eventHandlers.setFamiliesData, eventHandlers.setUnsavedChanges])

/* ------------------------------------------------------------------------------------------------------------------ */
/* MEMOIZED SUBCOMPONENTS                                                                                             */
/* ------------------------------------------------------------------------------------------------------------------ */

    const localeNameColumns = useMemo(() => {
        switch (intl.locale) {
            case Locale.ES:
                return [
                    createTextCell("surname1", rowIndex, 100, eventHandlers.onChange!, row.surname1),
                    createTextCell("surname2", rowIndex, 100, eventHandlers.onChange!, row.surname2)
                ]
            default:
                return [
                    createTextCell("middleName", rowIndex, 100, eventHandlers.onChange!, row.middleName),
                    createTextCell("surname1", rowIndex, 100, eventHandlers.onChange!, row.surname1)
                ]
        }
    }, [intl.locale, rowIndex, row.middleName, row.surname1, row.surname2, eventHandlers.onChange])

    const sexDropdownClass = useMemo(() => `selection sex-dropdown ${row.sex === Sex.M ? "male-bg" : row.sex === "F" ? "female-bg" : ""}`, [row.sex])

    const fatherOptions = useRelatedIndividualOptions(data, row.id, Sex.M, fullNames)
    const motherOptions = useRelatedIndividualOptions(data, row.id, Sex.F, fullNames)

    const relatedFamilies = useMemo(() =>
        (familiesData ?? []).reduce((map: Record<string, GedcomFamily>, fam: GedcomFamily) => {
            if (fam.husbandId && fam.wifeId) map[`${fam.husbandId}-${fam.wifeId}`] = fam
            return map
        }, {} as Record<string, GedcomFamily>),
    [familiesData])

    const relatedFamily = useMemo(() => {
        if (!row.fatherId && !row.motherId) return undefined                // no parents
        return relatedFamilies[ `${row.fatherId}-${row.motherId}`] ?? null  // parents exist but no such family
    }, [row.fatherId, row.motherId, relatedFamilies])

    const individualsMap = useMemo(() => new Map(data.map(i => [i.id, i])), [data])

    const husbandName = useMemo(() => {
        if (!relatedFamily?.husbandId) return undefined
        const husband = individualsMap.get(relatedFamily.husbandId)
        return husband ? GedcomIndividual.getFullName(husband, intl.locale, false) ?? husband.id : relatedFamily.husbandId
    }, [relatedFamily?.husbandId, individualsMap, intl.locale])

    const wifeName = useMemo(() => {
        if (!relatedFamily?.wifeId) return undefined
        const wife = individualsMap.get(relatedFamily.wifeId)
        return wife ? GedcomIndividual.getFullName(wife, intl.locale, false) ?? wife.id : relatedFamily.wifeId
    }, [relatedFamily?.wifeId, individualsMap, intl.locale])

    const familyClassName = useMemo(() =>
        `padded no-border-left fam-id ${relatedFamily !== undefined ? (relatedFamily && relatedFamily.enabled ? "fam-id-active" : "fam-id-inactive") : ""}`,
    [relatedFamily])

    const familyPendingTooltip = useMemo(() => {
        const familyIconColor = !row.fatherId || !row.motherId ? "white" : "teal"
        const familyTooltipContent = <div className="related-family-tooltip">
            {row.fatherId && row.motherId ?
                // Parents present, but no family
                <>
                    <span style={{ whiteSpace: "nowrap" }}>
                        <FormattedMessage id="creator.tooltip.family.missing"/><br/>
                        <FormattedMessage id="creator.tooltip.family.ask"/>
                    </span>
                    <Button color="teal" style={{marginTop: "10px"}} onClick={handleStartFamily} content={
                        <FormattedMessage id="creator.tooltip.family.start"/>
                    }/>
                </>
                // Only one parent assigned
                : <span><FormattedMessage id="creator.tooltip.family.spouse" values={{
                    parent: <FormattedMessage id="creator.tooltip.parent.placeholder"/>,
                    family: <span style={{ color: "var(--creator-fam-teal)" }}>
                        <FormattedMessage id="creator.tooltip.family.placeholder"/>
                    </span>
                    }}
                /></span>
            }
        </div>
        return (
            <Popup
                wide
                pinned
                inverted
                hoverable
                on="hover"
                size="large"
                position="top center"
                offset={[-12, 5]}
                style={{ marginLeft: "1.5em"}}
                trigger={<Icon name="users" style={{ display: "inline-block", width: "100%", color: familyIconColor }} />}
                content={familyTooltipContent}
            />
        )
    }, [handleStartFamily, row.fatherId, row.motherId])

    const familyTooltip = useMemo(() => {
        if (!relatedFamily) return
        return (
            <p>
                <FormattedMessage id="creator.tooltip.family.names" values={{
                    husbandName: <span className="tooltip male">{husbandName}</span>,
                    wifeName: <span className="tooltip female">{wifeName}</span>
                }}
                />&nbsp;
                <FormattedMessage id="creator.tooltip.family.children" values={{
                    nChildren: <span style={{ fontWeight: "bold" }}>{relatedFamily!.children.size}</span>
                }}/>
            </p>
        )
    }, [relatedFamily, husbandName, wifeName])

/* ------------------------------------------------------------------------------------------------------------------ */
/* ROW COLUMNS                                                                                                        */
/* ------------------------------------------------------------------------------------------------------------------ */

    return (
        // Key by row version so key changes (like bumpIdsAfter) remount the row
        <TableRow
            id={`indiRow-${row.id}`}
            key={`${row.id}-${rowVersion.current}`}
            className={`${row.enabled ? "" : "disabled-row"} ${selectedRowId === row.id ? "row-selected" : ""}`}
        >

            {/* Active */}
            <TableCell className="enabled-column">
                <Checkbox
                    toggle
                    checked={row.enabled}
                    onChange={(_, data) => handleEnabledChange(data.checked ?? false)}
                    style={{ marginLeft: "10px", marginRight: "10px" }}
                />
            </TableCell>

            {/* Individual ID */}
            <TableCell
                className={`id padded ${row.enabled ? "indi-id-active" : "indi-id-inactive"}`}
                data-value={shortenId(row.id)}
                onClick={() => setSelectedRowId(selectedRowId === row.id ? undefined : row.id)}
            />

            {/* Sex */}
            <TableCell className="sex">
                <Dropdown
                    clearable
                    options={sexOptions}
                    className={sexDropdownClass}
                    value={row.sex || undefined}
                    trigger={row.sex === Sex.M ? <Icon name="male"/> : row.sex === Sex.F ? <Icon name="female"/> : undefined}
                    onChange={(_, { value }) => handleSexChange(value as Sex | undefined)}
                />
            </TableCell>

            {/* Name */}
            {createTextCell("givenName", rowIndex, 100, eventHandlers.onChange!, row.givenName)}

            {/* Full name pattern locale-specific columns */}
            {localeNameColumns}

            {/* Birth date */}
            {createDateCell("birthDate", rowIndex, eventHandlers.onChange!, row.birthDate, "date-column")}

            {/* Birth place */}
            <TableCell className="birthPlace">
                <CreatorLocationCell
                    maxLength={100}
                    value={row.birthPlace ?? undefined}
                    onChange={handleBirthPlaceChange}
                    inputClassname="birthPlace"
                    popperClassname="from-start"
                    autocomplete={!from}
                />
            </TableCell>

            {/* Decease date */}
            {createDateCell("deceaseDate", rowIndex, eventHandlers.onChange!, row.deceaseDate, "date-column")}

            {/* Decease place */}
            <TableCell className="deceasePlace">
                <CreatorLocationCell
                    maxLength={100}
                    value={row.deceasePlace ?? undefined}
                    onChange={handleDeceasePlaceChange}
                    inputClassname="deceasePlace"
                    popperClassname="until-end"
                    autocomplete={!from}
                />
            </TableCell>

            {/* Father ID */}
            <SelectCell
                field="fatherId"
                rowIndex={rowIndex}
                options={fatherOptions}
                placeholder=""
                onChange={onParentChange}
                value={row.fatherId}
                dropdownClassName={`parents-dropdown ${row.fatherId ? "male-bg" : ""}`}
                disabled={!familiesEnabled}
            />
            {/* Mother ID */}
            <SelectCell
                field="motherId"
                rowIndex={rowIndex}
                options={motherOptions}
                placeholder=""
                onChange={onParentChange}
                value={row.motherId}
                dropdownClassName={`parents-dropdown ${row.motherId ? "female-bg" : ""}`}
                cellClassName="no-border-left"
                disabled={!familiesEnabled}
            />

            {/* Family ID */}
            <TableCell data-value={relatedFamily?.id} className={familyClassName}>
                {relatedFamily === undefined
                    ? null
                    : relatedFamily === null
                        ? familyPendingTooltip
                        : <Popup
                            flowing
                            hoverable
                            inverted
                            size="large"
                            wide="very"
                            position="top right"
                            offset={[-10, 5]}
                            content={familyTooltip}
                            trigger={<Button className="switch-tab-button" onClick={() => eventHandlers.switchTab?.(`famRow-${relatedFamily.id}`)}/>}
                        />
                }
            </TableCell>

            {/* Notes */}
            {createTextCell("notes", rowIndex, 500, eventHandlers.onChange!, row.notes, "notes")}

            {/* Remove */}
            <TableCell className="remove-column">
                {/* !row.pristine && */ <Button icon="trash" onClick={(e) => { setRowToRemove(row); setRemoveAnchor(e.currentTarget) }}/>}
            </TableCell>
            {rowToRemove && <Popup
                open={true}
                pinned
                flowing
                inverted
                on="click"
                size="large"
                position="left center"
                context={removeAnchor}
                onClose={() => {
                    setRowToRemove(undefined)
                    setRemoveAnchor(undefined)
                }}
                content={
                    <ConfirmRemove
                        rowType={GedcomIndividual}
                        row={rowToRemove}
                        individualsData={data}
                        familiesData={familiesData}
                        childrenMap={childrenMap}
                        spousesMap={spousesMap}
                        fullNames={fullNames}
                        onConfirmRemove={() => handleConfirmRemove(rowToRemove)}/>
                    }
                />
            }

        </TableRow>
    )
})